<template>
  <!-- Modal pre zadanie podnetu - otvára ho položka „Nahlásiť chybu“ v ľavom paneli (feedbackDialogOpen) -->
  <div class="feedback-widget">
    <v-dialog
      v-model="dialog"
      :max-width="620"
      :fullscreen="$vuetify.display.xs"
      scrollable
    >
      <v-card>
        <div class="dialog-head">
          <div>
            <v-card-title>Nahlásiť podnet</v-card-title>
            <div class="text-body-2 text-medium-emphasis">
              Napíšte, čo nefunguje alebo čo by ste zmenili. Stránku, na ktorej ste, priložíme automaticky.
            </div>
          </div>
          <v-btn icon variant="text" size="small" aria-label="Zavrieť" @click="closeDialog">
            <v-icon icon="mdi-close" size="20" />
          </v-btn>
        </div>

        <v-card-text>
          <!-- Typ podnetu -->
          <div class="feedback-widget__label">O čo ide?</div>
          <v-btn-toggle
            v-model="form.typ"
            mandatory
            class="feedback-widget__types mb-5"
          >
            <v-btn
              v-for="typ in typyPodnetu"
              :key="typ.value"
              :value="typ.value"
              size="small"
            >
              <v-icon :icon="typ.icon" start size="16" />
              {{ typ.label }}
            </v-btn>
          </v-btn-toggle>

          <!-- Text podnetu -->
          <v-textarea
            v-model="form.obsah"
            :label="aktualnyTyp.placeholder"
            rows="5"
            auto-grow
            counter="2000"
            maxlength="2000"
            :error-messages="obsahError"
            class="mb-2"
            @paste="handlePaste"
            @update:model-value="obsahError = ''"
          ></v-textarea>

          <!-- Obrázky -->
          <v-file-input
            v-model="files"
            label="Priložiť obrázok (voliteľné)"
            multiple
            show-size
            prepend-icon=""
            prepend-inner-icon="mdi-image-outline"
            accept="image/png,image/jpeg,image/gif,image/webp"
            :error-messages="fileError"
            hint="Screenshot môžete vložiť aj priamo klávesami Ctrl+V do textového poľa. Max 3 obrázky, každý do 10 MB."
            persistent-hint
            class="mb-3"
          >
            <template v-slot:selection="{ fileNames }">
              <v-chip
                v-for="name in fileNames"
                :key="name"
                size="small"
                variant="tonal"
                color="secondary"
                label
                class="mr-1"
              >
                {{ name }}
              </v-chip>
            </template>
          </v-file-input>

          <!-- Náhľady priložených obrázkov -->
          <div v-if="previews.length" class="feedback-widget__previews">
            <div
              v-for="(preview, index) in previews"
              :key="index"
              class="feedback-widget__preview"
            >
              <v-img
                :src="preview.url"
                :alt="preview.name"
                width="72"
                height="72"
                cover
                class="feedback-widget__preview-img"
              ></v-img>
              <button
                type="button"
                class="feedback-widget__preview-remove"
                :aria-label="`Odstrániť obrázok ${preview.name}`"
                @click="removeFile(index)"
              >
                <v-icon icon="mdi-close" size="12" />
              </button>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="loading" @click="closeDialog">Zrušiť</v-btn>
          <v-btn
            color="primary"
            prepend-icon="mdi-send-outline"
            :loading="loading"
            @click="submit"
          >
            Odoslať
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useNotificationStore } from '@/store/notification';
import SystemFeedbackService from '@/services/SystemFeedbackService';
import { feedbackDialogOpen } from '@/composables/useAppNavigation';

const MAX_FILES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB - rovnaký limit ako na backende

export default defineComponent({
  name: 'FeedbackWidget',

  setup() {
    const route = useRoute();
    const notificationStore = useNotificationStore();

    const dialog = feedbackDialogOpen;
    const loading = ref(false);
    const files = ref([]);
    const previews = ref([]);
    const obsahError = ref('');
    const fileError = ref('');

    const typyPodnetu = [
      {
        value: 'Nefunguje',
        label: 'Nefunguje',
        icon: 'mdi-bug-outline',
        placeholder: 'Popíšte, čo nefunguje a čo ste sa snažili urobiť'
      },
      {
        value: 'Návrh na zmenu',
        label: 'Návrh na zmenu',
        icon: 'mdi-lightbulb-on-outline',
        placeholder: 'Popíšte, čo by ste chceli zmeniť alebo doplniť'
      },
      {
        value: 'Iné',
        label: 'Iné',
        icon: 'mdi-comment-outline',
        placeholder: 'Popíšte váš podnet'
      }
    ];

    // Prioritu nezadáva používateľ - backend nastaví 'stredná' a vývojár ju doladí pri triáži
    const form = ref({
      typ: 'Nefunguje',
      obsah: ''
    });

    const aktualnyTyp = computed(
      () => typyPodnetu.find(t => t.value === form.value.typ) || typyPodnetu[2]
    );

    // Náhľady sa generujú z vybraných súborov
    const rebuildPreviews = () => {
      previews.value.forEach(p => URL.revokeObjectURL(p.url));
      previews.value = files.value.map(file => ({
        name: file.name,
        url: URL.createObjectURL(file)
      }));
    };

    // Validácia počtu a veľkosti súborov
    const validateFiles = () => {
      fileError.value = '';

      if (files.value.length > MAX_FILES) {
        fileError.value = `Maximálne ${MAX_FILES} obrázky`;
        return false;
      }

      if (files.value.some(file => file.size > MAX_FILE_SIZE)) {
        fileError.value = 'Každý obrázok musí byť menší ako 10 MB';
        return false;
      }

      return true;
    };

    watch(files, () => {
      validateFiles();
      rebuildPreviews();
    });

    const removeFile = index => {
      files.value = files.value.filter((_, i) => i !== index);
    };

    // Vloženie screenshotu priamo z clipboardu (Ctrl+V / Cmd+V)
    const handlePaste = event => {
      const items = Array.from(event.clipboardData?.items || []);
      const images = items
        .filter(item => item.type.startsWith('image/'))
        .map(item => item.getAsFile())
        .filter(Boolean);

      if (!images.length) return;

      const renamed = images.map(
        (file, i) =>
          new File([file], `screenshot-${Date.now()}-${i}.${file.type.split('/')[1] || 'png'}`, {
            type: file.type
          })
      );

      files.value = [...files.value, ...renamed].slice(0, MAX_FILES);
    };

    // Nadpis odvodíme z prvého riadku textu, aby používateľ vypĺňal len jedno pole
    const derivujNadpis = obsah => {
      const prvyRiadok = obsah.trim().split('\n')[0].trim();
      const nadpis = prvyRiadok.length > 90 ? `${prvyRiadok.slice(0, 90)}…` : prvyRiadok;
      return nadpis || 'Podnet z intranetu';
    };

    const resetForm = () => {
      previews.value.forEach(p => URL.revokeObjectURL(p.url));
      previews.value = [];
      files.value = [];
      form.value = { typ: 'Nefunguje', obsah: '' };
      obsahError.value = '';
      fileError.value = '';
    };

    const closeDialog = () => {
      dialog.value = false;
      resetForm();
    };

    const submit = async () => {
      const obsah = form.value.obsah.trim();

      if (obsah.length < 5) {
        obsahError.value = 'Napíšte prosím aspoň krátky popis (min. 5 znakov)';
        return;
      }

      if (!validateFiles()) return;

      loading.value = true;
      try {
        // Stránku posielame ako samostatné pole - vývojár tak vidí, kde chyba nastala
        await SystemFeedbackService.create(
          {
            nadpis: derivujNadpis(obsah),
            obsah,
            typ: form.value.typ,
            stranka: route.fullPath || '/'
          },
          files.value
        );

        notificationStore.success('Ďakujeme, podnet bol odoslaný.');
        closeDialog();
      } catch (error) {
        const message =
          error.response?.data?.message || 'Podnet sa nepodarilo odoslať. Skúste to znova.';
        notificationStore.error(message);
        console.error('Chyba pri odosielaní podnetu:', error);
      } finally {
        loading.value = false;
      }
    };

    return {
      dialog,
      loading,
      form,
      files,
      previews,
      obsahError,
      fileError,
      typyPodnetu,
      aktualnyTyp,
      closeDialog,
      submit,
      removeFile,
      handlePaste
    };
  }
});
</script>

<style lang="scss" scoped>
.feedback-widget {
  &__label {
    margin-bottom: 8px;
    font-size: 0.8125rem;
    font-weight: 650;
    color: var(--text-secondary);
  }

  &__types {
    max-width: 100%;

    .v-btn {
      flex: 1 1 auto;
    }
  }

  &__previews {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 4px 0 8px;
  }

  &__preview {
    position: relative;

    &-img {
      border-radius: var(--radius-control);
      box-shadow: 0 0 0 1px var(--border-subtle);
    }

    &-remove {
      position: absolute;
      top: -6px;
      right: -6px;
      display: grid;
      place-items: center;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: var(--text-primary);
      color: var(--surface);
      box-shadow: 0 0 0 2px var(--surface);
      transition: background-color var(--duration-fast) ease;

      &:hover,
      &:focus-visible {
        background: rgb(var(--v-theme-error));
      }
    }
  }
}

@media (max-width: 599px) {
  .feedback-widget__types {
    width: 100%;
  }
}
</style>
